import {useCallback, useState} from 'react';
import {useAskCallQuestionMutation, useFetchCallQuestionsQuery, type CallQuestion} from '../../../../store/features/calls/callsService';
import {errorStatus} from '../insightsErrors';
import {logQuestionAsked} from '../insightsLog';

export const QUESTION_MAX_LENGTH = 500;

export interface PendingQuestion {
    question: string;
    error: unknown;
}

export const useCallQuestions = (callId: number, enabled: boolean) => {
    const {data: history = [], isLoading, isError, refetch} = useFetchCallQuestionsQuery(callId, {skip: !enabled});
    const [askMutation] = useAskCallQuestionMutation();
    const [pending, setPending] = useState<PendingQuestion | null>(null);
    const [asking, setAsking] = useState(false);

    const ask = useCallback(async (raw: string): Promise<CallQuestion | null> => {
        const question = raw.trim().slice(0, QUESTION_MAX_LENGTH);
        if (!question) return null;
        setPending({question, error: null});
        setAsking(true);
        try {
            const answer = await askMutation({callId, question}).unwrap();
            setPending(null);
            logQuestionAsked({callId, status: 201, answerable: answer.answerable, citationCount: answer.citations?.length ?? 0});
            return answer;
        } catch (error) {
            setPending({question, error});
            logQuestionAsked({callId, status: errorStatus(error) ?? 'unknown', answerable: null, citationCount: 0});
            return null;
        } finally {
            setAsking(false);
        }
    }, [askMutation, callId]);

    const dismiss = useCallback(() => setPending(null), []);

    return {history, loading: isLoading, failed: isError, refetch, pending, asking, ask, dismiss};
};
